import philosophyHeroBg from "../assets/Philosophyhero.png";
import philosophyImage from "../assets/historyhero.png";
import {
  HiAcademicCap,
  HiCalendarDays,
  HiHeart,
  HiShieldCheck,
} from "react-icons/hi2";
import { FaBullseye, FaEye } from "react-icons/fa";

const coreValues = [
  {
    id: "flexibility",
    title: "Student-First Flexibility",
    Icon: HiCalendarDays,
    description:
      "We build learning around life, training, and travel, ensuring students never feel locked into a rigid timetable.",
  },
  {
    id: "excellence",
    title: "Academic Excellence",
    Icon: HiAcademicCap,
    description:
      "Flexibility without compromise. We uphold rigorous standards and recognized pathways (NIOS, CBSE, Cambridge) to guarantee university readiness.",
  },
  {
    id: "support",
    title: "Empathetic Support",
    Icon: HiHeart,
    description:
      "We provide a joyful environment driven by expert facilitators who act as mentors, not just teachers.",
  },
  {
    id: "integrity",
    title: "Integrity & Trust",
    Icon: HiShieldCheck,
    description:
      "From transparent progress tracking to fully compliant, secure examinations, we maintain the highest standards of operational and academic integrity.",
  },
];

function Philosophy() {
  return (
    <div className="bg-white">
      <section
        className="relative isolate flex min-h-[180px] items-center justify-center overflow-hidden bg-cover bg-center bg-no-repeat sm:min-h-[200px] md:min-h-[220px]"
        style={{ backgroundImage: `url(${philosophyHeroBg})` }}
        aria-labelledby="philosophy-hero-heading"
      >
        <div
          className="absolute inset-0 bg-gradient-to-r from-ink/70 via-ink/50 to-ink/35"
          aria-hidden
        />
        <div className="relative z-10 mx-auto max-w-7xl px-4 py-6 text-center sm:px-8 sm:py-8 md:px-10 lg:px-8">
          <h1
            id="philosophy-hero-heading"
            className="text-3xl font-semibold tracking-tight text-white sm:text-4xl md:text-5xl"
          >
            School Philosophy
          </h1>
        </div>
      </section>

      <section
        className="relative overflow-hidden bg-white py-16 sm:py-20 lg:py-24"
        aria-labelledby="philosophy-intro-heading"
      >
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-brand-light/25 to-transparent"
          aria-hidden
        />

        <div className="relative mx-auto max-w-7xl px-4 sm:px-8 md:px-10 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-2 lg:items-center lg:gap-14 xl:gap-16">
            <figure className="relative min-h-0 h-64 w-full sm:h-72 lg:h-[min(100%,28rem)] lg:min-h-[18rem]">
              <div className="relative h-full min-h-[16rem] overflow-hidden rounded-2xl border border-brand/10 bg-brand-light/30 shadow-[0_18px_44px_-16px_rgba(30,22,51,0.14)] ring-1 ring-brand/5">
                <img
                  src={philosophyImage}
                  alt="Students learning together in a bright classroom at Altus Online School"
                  className="absolute inset-0 h-full w-full object-cover object-center"
                  width={1200}
                  height={800}
                  loading="lazy"
                />
              </div>
            </figure>

            <header className="min-w-0 max-w-2xl text-left lg:max-w-none xl:pl-2">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-brand sm:text-[0.8125rem]">
                What we believe
              </p>
              <h2
                id="philosophy-intro-heading"
                className="mt-4 text-balance text-3xl font-bold tracking-tight text-ink sm:mt-5 sm:text-4xl md:text-[2.375rem] md:leading-tight"
              >
                Education That Adapts to You
              </h2>
              <p className="mt-6 text-pretty text-base leading-relaxed text-ink/75 sm:text-lg">
                At Altus Online School (AOS), we believe that education should adapt
                to the student, not the other way around. Our philosophy is rooted
                in a simple truth: academic excellence and personal ambition should
                never have to compete. We are building a new paradigm of learning
                where students are empowered to chase their passions, master their
                academics, and build a future on their own terms.
              </p>
            </header>
          </div>
        </div>
      </section>

      <section
        className="relative overflow-hidden bg-gradient-to-b from-brand-light/25 via-brand-light/40 to-brand-light/25 py-16 sm:py-20 lg:py-24"
        aria-labelledby="philosophy-vision-mission-heading"
      >
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-white to-transparent"
          aria-hidden
        />

        <div className="relative mx-auto max-w-7xl px-4 sm:px-8 md:px-10 lg:px-8">
          <header className="mx-auto max-w-3xl text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-brand sm:text-[0.8125rem]">
              Purpose &amp; direction
            </p>
            <h2
              id="philosophy-vision-mission-heading"
              className="mt-4 text-balance text-3xl font-bold tracking-tight text-ink sm:mt-5 sm:text-4xl md:text-[2.375rem] md:leading-tight"
            >
              Our Vision &amp; Mission
            </h2>
            <p className="mt-6 text-pretty text-base leading-relaxed text-ink/75 sm:text-lg">
              Our vision sets the horizon a borderless campus where every child can
              excel without sacrificing who they are. Our mission is how we get
              there: inclusive, flexible learning backed by expert educators and
              unwavering trust.
            </p>
          </header>

          <div className="mt-14 grid gap-8 sm:mt-16 lg:grid-cols-2">
            <article className="rounded-2xl border border-brand/10 bg-white p-7 shadow-[0_16px_40px_-24px_rgba(30,22,51,0.22)] ring-1 ring-brand/5 sm:p-8">
              <h3 className="text-center text-2xl font-bold tracking-tight text-ink sm:text-3xl">
                Our Vision
              </h3>
              <div className="mt-5 flex justify-center text-brand">
                <span className="flex h-20 w-20 items-center justify-center rounded-full border border-brand/15 bg-brand-light ring-1 ring-brand/10">
                  <FaEye className="h-10 w-10" aria-hidden />
                </span>
              </div>
              <p className="mt-6 text-center text-pretty text-base leading-relaxed text-ink/75 sm:text-lg">
                To build a borderless virtual campus where globally recognized
                education is accessible to every child. We envision a future where
                every learner regardless of their geography, lifestyle, or
                passions can achieve academic excellence without ever having to
                sacrifice their personal ambitions.
              </p>
            </article>

            <article className="rounded-2xl border border-brand/10 bg-white p-7 shadow-[0_16px_40px_-24px_rgba(30,22,51,0.22)] ring-1 ring-brand/5 sm:p-8">
              <h3 className="text-center text-2xl font-bold tracking-tight text-ink sm:text-3xl">
                Our Mission
              </h3>
              <div className="mt-5 flex justify-center text-brand">
                <span className="flex h-20 w-20 items-center justify-center rounded-full border border-brand/15 bg-brand-light ring-1 ring-brand/10">
                  <FaBullseye className="h-10 w-10" aria-hidden />
                </span>
              </div>
              <p className="mt-6 text-center text-pretty text-base leading-relaxed text-ink/75 sm:text-lg">
                To deliver an inclusive, flexible education that honors the unique
                needs of every individual learner. We are committed to providing
                personalized academic pathways, certified expert educators, and an
                unshakeable foundation of trust and compliance. We don&apos;t just
                teach; we equip future-ready students to balance life and learning
                with confidence.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section
        className="relative overflow-hidden bg-gradient-to-b from-brand-light/30 via-brand-light/45 to-brand-light/70 py-16 sm:py-20 lg:py-24"
        aria-labelledby="philosophy-values-heading"
      >
        <div
          className="pointer-events-none absolute left-1/2 top-0 h-[28rem] w-[min(90vw,52rem)] -translate-x-1/2 rounded-full bg-brand/[0.07] blur-3xl"
          aria-hidden
        />

        <div className="relative mx-auto max-w-7xl px-4 sm:px-8 md:px-10 lg:px-8">
          <header className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-brand sm:text-[0.8125rem]">
              What guides us
            </p>
            <h2
              id="philosophy-values-heading"
              className="mt-4 text-balance text-3xl font-bold tracking-tight text-ink sm:mt-5 sm:text-4xl md:text-[2.375rem] md:leading-tight"
            >
              Our Core Values
            </h2>
          </header>

          <div className="mt-14 grid gap-5 sm:mt-16 sm:grid-cols-2 sm:gap-6 lg:gap-7">
            {coreValues.map(({ id, title, Icon, description }) => (
              <article
                key={id}
                className="group relative flex h-full flex-col overflow-hidden rounded-3xl bg-white/90 p-6 shadow-[0_1px_0_0_rgba(59,43,131,0.06),0_18px_44px_-16px_rgba(30,22,51,0.18)] ring-1 ring-brand/8 backdrop-blur-sm transition duration-300 hover:-translate-y-0.5 hover:shadow-[0_1px_0_0_rgba(59,43,131,0.08),0_24px_56px_-18px_rgba(59,43,131,0.22)] hover:ring-brand/15 sm:p-7"
              >
                <div
                  className="pointer-events-none absolute -right-12 -top-12 h-36 w-36 rounded-full bg-gradient-to-br from-brand/[0.09] to-transparent opacity-0 transition duration-500 group-hover:opacity-100"
                  aria-hidden
                />
                <div className="relative flex items-start gap-4">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-brand to-brand-dark text-white shadow-md shadow-brand/25">
                    <Icon className="h-6 w-6" aria-hidden />
                  </span>
                  <div className="min-w-0 flex-1 pt-0.5">
                    <h3 className="text-lg font-bold leading-snug tracking-tight text-ink sm:text-xl">
                      {title}
                    </h3>
                    <p className="mt-4 text-pretty text-sm leading-relaxed text-ink/72 sm:text-[0.9375rem]">
                      {description}
                    </p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export default Philosophy;
